import { isDefined } from '@aloft/toolkit-shared';

import { countBy } from 'lodash';

import { useVacantLandSales } from '../../../hooks/useVacantLandSales.tsx';
import {
  ExponentialFitDataPoint,
  GroupedVacantLandSalesChartData,
  SubjectVacantLandSalesChartData,
  VacantLandSalesChartData,
} from './types.ts';

export const getVacantLandChartData = (
  filteredVacantLandSales: ReturnType<typeof useVacantLandSales>,
): VacantLandSalesChartData[] | undefined => {
  if (!filteredVacantLandSales) return undefined;

  return filteredVacantLandSales
    .map(({ address, distanceToSubject, lotSize, saleDate, salePrice }) => {
      const costPerSF =
        salePrice && lotSize
          ? salePrice.div(lotSize.getUnitAmount('sqft')).toDollarLike()
          : undefined;

      if (!lotSize || !costPerSF) return undefined;

      return {
        address: address?.AddressLineText,
        saleDate: saleDate?.toString(),
        siteSize: lotSize?.getUnitAmount('sqft'),
        costPerSF,
        distanceToSubject,
      };
    })
    .filter(isDefined);
};

export const groupVacantLandSalesData = (
  data: VacantLandSalesChartData[],
): GroupedVacantLandSalesChartData[] => {
  const totalCount = data.length;

  const counts = countBy(
    data.map((d) => d.costPerSF),
    Math.floor,
  );

  const groups = Object.entries(counts).map(([costPerSF, count]) => ({
    costPerSF: Number(costPerSF),
    percentage: Number(((count / totalCount) * 100).toFixed(2)),
  }));

  return groups;
};

/**
 * Fits an exponential curve to vacant land sales data: cost per SF vs site size (sqft).
 *
 * Formula: y = a * exp(b * x)
 *   - x: site size (sqft)
 *   - y: cost per SF
 *   - a, b: calculated from the data so the curve passes through the subject point
 *
 * Returns:
 *   - `lineData`: curve points for charts
 *   - `getCostPerSFFromSiteSize(siteSize)`: function that gives the fitted cost/SF for a given size
 *
 * Expects: filtered, valid sales data (no zeros or negatives).
 */
export const getVacantLandSalesExponentialFit = (
  vacantLandSalesData: VacantLandSalesChartData[],
  subjectDataPoint: SubjectVacantLandSalesChartData,
): {
  lineData: ExponentialFitDataPoint[];
  getCostPerSFFromSiteSize: (siteSize: number) => number;
} => {
  const xValues = vacantLandSalesData.map((d) => d.siteSize);
  const yValues = vacantLandSalesData.map((d) => d.costPerSF);
  const logYValues = yValues.map((y) => Math.log(y));

  const meanX = xValues.reduce((sum, val) => sum + val, 0) / xValues.length;
  const meanY = logYValues.reduce((sum, val) => sum + val, 0) / logYValues.length;

  const covXY =
    xValues.reduce((sum, x, i) => sum + (x - meanX) * (logYValues[i]! - meanY), 0) / xValues.length;

  const varX = xValues.reduce((sum, x) => sum + (x - meanX) ** 2, 0) / xValues.length;

  const b = Math.abs(covXY / varX) * -1;
  const a = subjectDataPoint.subjectCostPerSF / Math.exp(b * subjectDataPoint.siteSize);

  const minX = 0;
  const maxX = Math.max(subjectDataPoint?.siteSize ?? 0, ...xValues);
  const numPoints = 50;
  const step = (maxX - minX) / (numPoints - 1);

  const lineData = Array.from({ length: numPoints }, (_, i) => {
    const x = minX + i * step;
    return {
      siteSize: x,
      lineY: a * Math.exp(b * x),
    };
  });

  return {
    lineData,
    getCostPerSFFromSiteSize: (siteSize: number) => a * Math.exp(b * siteSize),
  };
};
